import React = require("react");
import { permissionHelper, PublicAmorgUser } from '../../../amorg-common';
import { fieldValidation } from '../../../amorg-common';
import { updateUser, updateUserInitials, updateUserStatus } from "../../../api/userClientApi";
import { TextSaveField } from "../../../designSystem/formField/text/TextSaveField";
import { H2 } from "../../../designSystem/typography/Heading";
import { Flex, FlexItem } from "../../../designSystem/compositeComponents/FlexHelpers";
import { P } from "../../../designSystem/typography/Body";
import { MessageCard } from "../../../designSystem/Message";
import { Card } from "../../../designSystem/Card";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { Button } from "../../../designSystem/button/Button";
import { ConfirmationModal } from "../../../designSystem/compositeComponents/ConfirmationButton";
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';


type UserDetailsEditorProps = {
    user: PublicAmorgUser;
    onUserUpdated: () => void;
};
export function UserDetailsEditor ({ user, onUserUpdated }: UserDetailsEditorProps): JSX.Element 
{
    const [error, setError] = React.useState('');
    const [showDeactivateConfirmation, setShowDeactivateConfirmation] = React.useState(false);


    const ctx = React.useContext(ProtectedPageContext);
    const canChangeStatus = permissionHelper.canAdminEditTargetGroupId(ctx.user.permissionGroupId, user.permissionGroup.id, ctx.user?.userId === user.id);

    async function handleSaveDetails({ firstName, middleName, lastName }: { firstName: string; middleName: string; lastName: string; })
    {
        if (!fieldValidation.user.firstname(firstName))
            setError('Ugyldigt fornavn. Skal udfyldes og må max være 100 tegn langt');

        else if(!fieldValidation.user.middlename(middleName))
            setError('Ugyldigt mellemnavn. Må max være 100 tegn langt');

        else if(!fieldValidation.user.lastname(lastName))
            setError('Ugyldigt efternavn. Skal udfyldes og må max være 100 tegn langt');

        else
        {
            const result = await updateUser(user.id, firstName, middleName, lastName);
            if (result.isSuccess)
            {
                onUserUpdated();
                setError('');
            }
            else
            {
                setError('Der skete en fejl ved opdateringen af brugerens detaljer - prøv igen senere');
            }
        }
    }

    async function handleSaveInitials (initials: string)
    {
        if (!fieldValidation.user.initials(initials)){
            setError('Ugyldige initialer - skal være 1-3 STORE bogstaver');
            return;
        }

        const result = await updateUserInitials(user.id, initials);
        if (result.isSuccess)
        {
            setError('');
            onUserUpdated();
        }
        else if (result.errorMessage === 'updateUserInitials_initials_in_use')
            setError('De valgte initialer er i brug af en anden bruger');
        else
            setError('Der skete en fejl ved opdateringen af initialer - prøv igen senere');
    }

    return (
        <>
            <FlexItem textAlign="center">
                <H2>{user.firstname + (user.middlename ? ` ${user.middlename} ` : ' ') + user.lastname}</H2>
                <P>{user.email}</P>
                {user.permissionGroup !== null &&
                    <P>{user.permissionGroup.name}</P>
                }
            </FlexItem>

            
            {!user.enabled &&
                <MessageCard variant="warning">
                    Brugeren er deaktiveret og kan ikke benytte systemet.
                </MessageCard>
            }

            <Flex flexDirection="column" gap={8}>
                <Card variant="outlined">

                    <Flex flexDirection="column" gap={16}>

                    {error &&
                        <MessageCard variant='error'>
                            {error}
                        </MessageCard>
                    }

                    <Flex flexDirection="row" gap={8}>
                        <div style={{ flex: 1 }}>
                        <TextSaveField
                            label="Fornavn"
                            validator={(currentValue) => fieldValidation.user.firstname(currentValue)
                                ? { valid: true }
                                : { valid: false, errorMessage: '' }}
                            value={user.firstname}
                            disabled={!user.enabled}
                            onSave={(firstName) => handleSaveDetails({ firstName, middleName: user.middlename, lastName: user.lastname })} />
                        </div>

                        <div style={{ flex: 1 }}>
                        <TextSaveField
                            label="Mellemnavn"
                            validator={(currentValue) => fieldValidation.user.middlename(currentValue)
                                ? { valid: true }
                                : { valid: false, errorMessage: '' }}
                            value={user.middlename}
                            disabled={!user.enabled}
                            onSave={(middleName) => handleSaveDetails({ firstName: user.firstname, middleName, lastName: user.lastname })} />
                        </div>

                        <div style={{ flex: 1 }}>
                        <TextSaveField
                            label="Efternavn"
                            validator={(currentValue) => fieldValidation.user.lastname(currentValue)
                                ? { valid: true }
                                : { valid: false, errorMessage: '' }}
                            value={user.lastname}
                            disabled={!user.enabled}
                            onSave={(lastName) => handleSaveDetails({ firstName: user.firstname, middleName: user.middlename, lastName })} />
                        </div>

                        <FlexItem width="75px">
                            <TextSaveField
                                label="Initialer"
                                value={user.initials}
                                disabled={!user.enabled}
                                onSave={(value) => { handleSaveInitials(value); }} />

                            {/* Initialer skal udfyldes med 1-3 STORE bogstaver. Feks. AC. Bemærk at initialerne ikke må være i brug af en anden bruger */}
                        </FlexItem>
                    </Flex>
                    </Flex>
                    
                </Card>
                {canChangeStatus &&
                    <ButtonRow>
                        {user.enabled &&
                            <>
                                <ConfirmationModal 
                                    showModal={showDeactivateConfirmation} 
                                    title={"Deaktiver bruger"} 
                                    content={
                                        <P>
                                            Du er ved at deaktivere en bruger.<br />
                                            <br />
                                            Brugeren vil ikke længere kunne logge ind, og vil ikke kunne vælges i forbindelse med brug af funktionalitet.<br />
                                            <br />
                                            Vi anbefaler at I ændrer brugerens navn, initialer samt evt. roller og email addresse, hvis I ønsker at sløre brugerens historiske aktivitet.<br />
                                            <br />
                                            Er du sikker på at du ønsker at fortsætte ?
                                        </P>
                                    } 
                                    requireKey={false} 
                                    buttonText={"Deaktiver brugeren"}
                                    onCancel={() => setShowDeactivateConfirmation(false)}
                                    onConfirm={async () => {
                                        setShowDeactivateConfirmation(false);
                                        const result = await updateUserStatus(user.id, false);
                                        if(result.isSuccess)
                                            onUserUpdated();
                                    }}
                                />

                                <Button variant="destructive" 
                                    text="Deaktiver bruger"
                                    onClick={() => { setShowDeactivateConfirmation(true); }} 
                                />
                            </>
                        }
                        {!user.enabled &&
                            <Button variant="filled" 
                                text="Aktiver bruger"
                                onClick={async () => {
                                    const result = await updateUserStatus(user.id, true);
                                    if(result.isSuccess)
                                        onUserUpdated();
                                }} 
                            />
                        }
                    </ButtonRow>
                }
            </Flex>
        </>
    );
}